/**
 * Pipeline Console Redux Actions
 *
 * All Pipeline API requests include the x-pipeline-env header to scope
 * data to the current environment (staging/prod).
 */

import axios from 'axios'
import { baseUrl } from 'urls'
import { getAxiosDefaultConfig } from 'utils'

import { PIPELINE_ENV_HEADER } from '../applications/Pipeline/config'
import { getPipelineEnv, setPipelineEnv } from '../applications/Pipeline/pipelineEnvStore'

/**
 * Get axios config with Pipeline environment header
 * Extends the default config with the x-pipeline-env header
 * Environment is read from pipelineEnvStore (synced from Redux when user toggles)
 */
const getPipelineAxiosConfig = () => {
  const defaultConfig = getAxiosDefaultConfig()
  return {
    ...defaultConfig,
    headers: {
      ...defaultConfig.headers,
      [PIPELINE_ENV_HEADER]: getPipelineEnv(),
    },
  }
}

// Action Types
export const PIPELINE_ACTIONS = {
  // Accounts
  FETCH_ACCOUNTS_PENDING: 'PIPELINE_FETCH_ACCOUNTS_PENDING',
  FETCH_ACCOUNTS_FULFILLED: 'PIPELINE_FETCH_ACCOUNTS_FULFILLED',
  FETCH_ACCOUNTS_REJECTED: 'PIPELINE_FETCH_ACCOUNTS_REJECTED',

  FETCH_ACCOUNT_DETAIL_PENDING: 'PIPELINE_FETCH_ACCOUNT_DETAIL_PENDING',
  FETCH_ACCOUNT_DETAIL_FULFILLED: 'PIPELINE_FETCH_ACCOUNT_DETAIL_FULFILLED',
  FETCH_ACCOUNT_DETAIL_REJECTED: 'PIPELINE_FETCH_ACCOUNT_DETAIL_REJECTED',

  // Contacts
  FETCH_CONTACTS_PENDING: 'PIPELINE_FETCH_CONTACTS_PENDING',
  FETCH_CONTACTS_FULFILLED: 'PIPELINE_FETCH_CONTACTS_FULFILLED',
  FETCH_CONTACTS_REJECTED: 'PIPELINE_FETCH_CONTACTS_REJECTED',

  // Merged people (contacts + PayHelm team)
  FETCH_PEOPLE_PENDING: 'PIPELINE_FETCH_PEOPLE_PENDING',
  FETCH_PEOPLE_FULFILLED: 'PIPELINE_FETCH_PEOPLE_FULFILLED',
  FETCH_PEOPLE_REJECTED: 'PIPELINE_FETCH_PEOPLE_REJECTED',

  // Threads
  FETCH_THREADS_PENDING: 'PIPELINE_FETCH_THREADS_PENDING',
  FETCH_THREADS_FULFILLED: 'PIPELINE_FETCH_THREADS_FULFILLED',
  FETCH_THREADS_REJECTED: 'PIPELINE_FETCH_THREADS_REJECTED',

  // Messages
  FETCH_MESSAGES_PENDING: 'PIPELINE_FETCH_MESSAGES_PENDING',
  FETCH_MESSAGES_FULFILLED: 'PIPELINE_FETCH_MESSAGES_FULFILLED',
  FETCH_MESSAGES_REJECTED: 'PIPELINE_FETCH_MESSAGES_REJECTED',

  // Events
  FETCH_EVENTS_PENDING: 'PIPELINE_FETCH_EVENTS_PENDING',
  FETCH_EVENTS_FULFILLED: 'PIPELINE_FETCH_EVENTS_FULFILLED',
  FETCH_EVENTS_REJECTED: 'PIPELINE_FETCH_EVENTS_REJECTED',

  // Operator State
  FETCH_OPERATOR_STATE_FULFILLED: 'PIPELINE_FETCH_OPERATOR_STATE_FULFILLED',
  UPDATE_OPERATOR_STATE_PENDING: 'PIPELINE_UPDATE_OPERATOR_STATE_PENDING',
  UPDATE_OPERATOR_STATE_FULFILLED: 'PIPELINE_UPDATE_OPERATOR_STATE_FULFILLED',
  UPDATE_OPERATOR_STATE_REJECTED: 'PIPELINE_UPDATE_OPERATOR_STATE_REJECTED',

  // Notes
  FETCH_NOTES_FULFILLED: 'PIPELINE_FETCH_NOTES_FULFILLED',
  ADD_NOTE_PENDING: 'PIPELINE_ADD_NOTE_PENDING',
  ADD_NOTE_FULFILLED: 'PIPELINE_ADD_NOTE_FULFILLED',
  ADD_NOTE_REJECTED: 'PIPELINE_ADD_NOTE_REJECTED',

  // Activities
  FETCH_ACTIVITIES_PENDING: 'PIPELINE_FETCH_ACTIVITIES_PENDING',
  FETCH_ACTIVITIES_FULFILLED: 'PIPELINE_FETCH_ACTIVITIES_FULFILLED',
  FETCH_ACTIVITIES_REJECTED: 'PIPELINE_FETCH_ACTIVITIES_REJECTED',
  CREATE_ACTIVITY_PENDING: 'PIPELINE_CREATE_ACTIVITY_PENDING',
  CREATE_ACTIVITY_FULFILLED: 'PIPELINE_CREATE_ACTIVITY_FULFILLED',
  CREATE_ACTIVITY_REJECTED: 'PIPELINE_CREATE_ACTIVITY_REJECTED',

  // Summaries
  FETCH_SUMMARY_FULFILLED: 'PIPELINE_FETCH_SUMMARY_FULFILLED',

  // Health
  FETCH_HEALTH_PENDING: 'PIPELINE_FETCH_HEALTH_PENDING',
  FETCH_HEALTH_FULFILLED: 'PIPELINE_FETCH_HEALTH_FULFILLED',
  FETCH_HEALTH_REJECTED: 'PIPELINE_FETCH_HEALTH_REJECTED',

  // Filters
  SET_FILTERS: 'PIPELINE_SET_FILTERS',
  SET_SORT: 'PIPELINE_SET_SORT',
  CLEAR_FILTERS: 'PIPELINE_CLEAR_FILTERS',

  // Environment
  SET_ENVIRONMENT: 'PIPELINE_SET_ENVIRONMENT',
  SET_ENV_INFO: 'PIPELINE_SET_ENV_INFO',

  // UI
  SET_SELECTED_THREAD: 'PIPELINE_SET_SELECTED_THREAD',
  SET_ACTIVE_TAB: 'PIPELINE_SET_ACTIVE_TAB',

  // Opportunities
  FETCH_OPPORTUNITIES_PENDING: 'PIPELINE_FETCH_OPPORTUNITIES_PENDING',
  FETCH_OPPORTUNITIES_FULFILLED: 'PIPELINE_FETCH_OPPORTUNITIES_FULFILLED',
  FETCH_OPPORTUNITIES_REJECTED: 'PIPELINE_FETCH_OPPORTUNITIES_REJECTED',
  FETCH_OPPORTUNITY_STATS_FULFILLED: 'PIPELINE_FETCH_OPPORTUNITY_STATS_FULFILLED',
  UPDATE_OPPORTUNITY_STATUS_FULFILLED: 'PIPELINE_UPDATE_OPPORTUNITY_STATUS_FULFILLED',

  // Advisor (Sales Co-Pilot)
  FETCH_ADVISOR_LATEST_PENDING: 'PIPELINE_FETCH_ADVISOR_LATEST_PENDING',
  FETCH_ADVISOR_LATEST_FULFILLED: 'PIPELINE_FETCH_ADVISOR_LATEST_FULFILLED',
  FETCH_ADVISOR_LATEST_REJECTED: 'PIPELINE_FETCH_ADVISOR_LATEST_REJECTED',
  GENERATE_ADVISOR_PENDING: 'PIPELINE_GENERATE_ADVISOR_PENDING',
  GENERATE_ADVISOR_FULFILLED: 'PIPELINE_GENERATE_ADVISOR_FULFILLED',
  GENERATE_ADVISOR_REJECTED: 'PIPELINE_GENERATE_ADVISOR_REJECTED',
  FETCH_ADVISOR_HISTORY_FULFILLED: 'PIPELINE_FETCH_ADVISOR_HISTORY_FULFILLED',

  // Advisor Chat
  SEND_ADVISOR_CHAT_PENDING: 'PIPELINE_SEND_ADVISOR_CHAT_PENDING',
  SEND_ADVISOR_CHAT_FULFILLED: 'PIPELINE_SEND_ADVISOR_CHAT_FULFILLED',
  SEND_ADVISOR_CHAT_REJECTED: 'PIPELINE_SEND_ADVISOR_CHAT_REJECTED',
  FETCH_ADVISOR_CHAT_HISTORY_FULFILLED: 'PIPELINE_FETCH_ADVISOR_CHAT_HISTORY_FULFILLED',
  CLEAR_ADVISOR_CHAT_HISTORY: 'PIPELINE_CLEAR_ADVISOR_CHAT_HISTORY',

  // Advisor Chat Conversations
  FETCH_CONVERSATIONS_FULFILLED: 'PIPELINE_FETCH_CONVERSATIONS_FULFILLED',
  CREATE_CONVERSATION_FULFILLED: 'PIPELINE_CREATE_CONVERSATION_FULFILLED',
  DELETE_CONVERSATION_FULFILLED: 'PIPELINE_DELETE_CONVERSATION_FULFILLED',
  SET_ACTIVE_CONVERSATION: 'PIPELINE_SET_ACTIVE_CONVERSATION',
  RENAME_CONVERSATION_FULFILLED: 'PIPELINE_RENAME_CONVERSATION_FULFILLED',
}

// API Base URL
const pipelineApiUrl = `${baseUrl}/pipeline`

// Account Actions
export const fetchAccounts = (filters = {}, sort = {}, limit = 100, offset = 0) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_ACCOUNTS_PENDING })

    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
      if (sort.field) params.append('sortField', sort.field)
      if (sort.direction) params.append('sortDirection', sort.direction)
      params.append('limit', String(limit))
      params.append('offset', String(offset))
      // Always include AI summary flags for the accounts list
      params.append('includeSummary', 'true')
      // Include opportunities for upsell badges
      params.append('includeOpportunities', 'true')

      const response = await axios.get(`${pipelineApiUrl}/accounts?${params.toString()}`, getPipelineAxiosConfig())

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ACCOUNTS_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ACCOUNTS_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

export const fetchAccountDetail = (accountId: string) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_ACCOUNT_DETAIL_PENDING })

    try {
      const response = await axios.get(`${pipelineApiUrl}/accounts/${accountId}`, getPipelineAxiosConfig())

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ACCOUNT_DETAIL_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ACCOUNT_DETAIL_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Fetch PayHelm Users (for import into the pipeline)
export const fetchPayHelmUsers = (options?: {
  limit?: number
  offset?: number
  search?: string
  unlinkedOnly?: boolean
  minRevenue?: number
  maxRevenue?: number
  plan?: string
  channel?: string
  hasOrders?: boolean
}) => {
  return async () => {
    try {
      const params = new URLSearchParams()
      if (options?.limit) params.append('limit', options.limit.toString())
      if (options?.offset) params.append('offset', options.offset.toString())
      if (options?.search) params.append('search', options.search)
      if (options?.unlinkedOnly !== undefined) params.append('unlinked_only', options.unlinkedOnly.toString())
      if (options?.minRevenue !== undefined) params.append('min_revenue', options.minRevenue.toString())
      if (options?.maxRevenue !== undefined) params.append('max_revenue', options.maxRevenue.toString())
      if (options?.plan) params.append('plan', options.plan)
      if (options?.channel) params.append('channel', options.channel)
      if (options?.hasOrders) params.append('has_orders', 'true')

      const response = await axios.get(
        `${pipelineApiUrl}/accounts/payhelm-users${params.toString() ? '?' + params.toString() : ''}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching PayHelm users', error)
      return { users: [], total: 0 }
    }
  }
}

/**
 * Add a PayHelm user to the pipeline (create pipeline account + operator state).
 * Body: { unique_user_id: string, email: string }
 */
export const addPayHelmUserToPipeline = (payload: { unique_user_id: string; email: string }) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/add-payhelm-user`,
        payload,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error) {
      console.error('Error adding PayHelm user to pipeline', error)
      throw error
    }
  }
}

// Contact Actions
export const fetchContacts = (accountId: string, limit = 50, offset = 0) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_CONTACTS_PENDING })

    try {
      const response = await axios.get(
        `${pipelineApiUrl}/contacts?account_id=${accountId}&limit=${limit}&offset=${offset}`,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_CONTACTS_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_CONTACTS_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Merged people (contacts + PayHelm team) for Company tab
export const fetchPeople = (accountId: string) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_PEOPLE_PENDING })
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/accounts/${accountId}/people`,
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_PEOPLE_FULFILLED,
        payload: response.data,
      })
      return response.data
    } catch (error) {
      dispatch({ type: PIPELINE_ACTIONS.FETCH_PEOPLE_REJECTED, payload: error })
      throw error
    }
  }
}

// Create a new contact
export const createContact = (contactData: {
  account_id: string
  name?: string
  email?: string
  role?: string
  title?: string
  phone?: string
  notes?: string
  is_primary?: boolean
}) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/contacts`, contactData, getPipelineAxiosConfig())

      // Refresh contacts list and merged people
      dispatch(fetchContacts(contactData.account_id))
      dispatch(fetchPeople(contactData.account_id))

      return { success: true, contact: response.data }
    } catch (error: any) {
      console.error('Error creating contact', error)
      return { success: false, error: error.response?.data?.error || 'Failed to create contact' }
    }
  }
}

// Update an existing contact
export const updateContact = (
  contactId: string,
  accountId: string,
  updates: {
    name?: string
    email?: string
    role?: string
    title?: string
    phone?: string
    notes?: string
    is_primary?: boolean
  }
) => {
  return async (dispatch) => {
    try {
      const response = await axios.put(`${pipelineApiUrl}/contacts/${contactId}`, updates, getPipelineAxiosConfig())

      // Refresh contacts list and merged people
      dispatch(fetchContacts(accountId))
      dispatch(fetchPeople(accountId))

      return { success: true, contact: response.data }
    } catch (error: any) {
      console.error('Error updating contact', error)
      return { success: false, error: error.response?.data?.error || 'Failed to update contact' }
    }
  }
}

// Delete a contact
export const deleteContact = (contactId: string, accountId: string) => {
  return async (dispatch) => {
    try {
      await axios.delete(`${pipelineApiUrl}/contacts/${contactId}`, getPipelineAxiosConfig())

      // Refresh contacts list
      dispatch(fetchContacts(accountId))

      return { success: true }
    } catch (error: any) {
      console.error('Error deleting contact', error)
      return { success: false, error: error.response?.data?.error || 'Failed to delete contact' }
    }
  }
}

// Thread Actions
export const fetchThreads = (accountId: string, limit = 50, offset = 0) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_THREADS_PENDING })

    try {
      const response = await axios.get(
        `${pipelineApiUrl}/threads?account_id=${accountId}&limit=${limit}&offset=${offset}`,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_THREADS_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_THREADS_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Message Actions
export const fetchMessages = (threadId: string, limit = 50) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_MESSAGES_PENDING })

    try {
      const response = await axios.get(
        `${pipelineApiUrl}/messages?thread_id=${threadId}&limit=${limit}`,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_MESSAGES_FULFILLED,
        payload: { threadId, ...response.data },
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_MESSAGES_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Event Actions
export const fetchEvents = (accountId: string, limit = 30) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_EVENTS_PENDING })

    try {
      const response = await axios.get(
        `${pipelineApiUrl}/events?account_id=${accountId}&limit=${limit}`,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_EVENTS_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_EVENTS_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Operator State Actions
export const fetchOperatorState = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/operator-state/${accountId}`, getPipelineAxiosConfig())

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_OPERATOR_STATE_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      console.error('Error fetching operator state', error)
      throw error
    }
  }
}

export const updateOperatorState = (accountId: string, updates: any) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.UPDATE_OPERATOR_STATE_PENDING })

    try {
      const response = await axios.patch(
        `${pipelineApiUrl}/operator-state/${accountId}`,
        updates,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.UPDATE_OPERATOR_STATE_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.UPDATE_OPERATOR_STATE_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Notes Actions
export const fetchNotes = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/notes?account_id=${accountId}`, getPipelineAxiosConfig())

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_NOTES_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      console.error('Error fetching notes', error)
      throw error
    }
  }
}

export const addNote = (accountId: string, body: string) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.ADD_NOTE_PENDING })

    try {
      const response = await axios.post(
        `${pipelineApiUrl}/notes`,
        { account_id: accountId, body },
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.ADD_NOTE_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.ADD_NOTE_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Activity Actions
export const fetchActivities = (accountId: string, limit = 50, offset = 0) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_ACTIVITIES_PENDING })

    try {
      const response = await axios.get(
        `${pipelineApiUrl}/activities?account_id=${accountId}&limit=${limit}&offset=${offset}`,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ACTIVITIES_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ACTIVITIES_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

export const createActivity = (
  accountId: string,
  data: {
    activity_type: string
    activity_subtype?: string
    direction?: string
    subject?: string
    notes?: string
    activity_date?: string
  }
) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.CREATE_ACTIVITY_PENDING })

    try {
      const response = await axios.post(
        `${pipelineApiUrl}/activities`,
        {
          account_id: accountId,
          ...data,
        },
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.CREATE_ACTIVITY_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      dispatch({
        type: PIPELINE_ACTIONS.CREATE_ACTIVITY_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Summary Actions
export const fetchLatestSummary = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/summaries/latest/account/${accountId}`,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_SUMMARY_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      console.error('Error fetching summary', error)
      throw error
    }
  }
}

// Health Actions
export const fetchPipelineHealth = () => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_HEALTH_PENDING })

    try {
      const response = await axios.get(`${pipelineApiUrl}/health`, getPipelineAxiosConfig())

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_HEALTH_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error) {
      console.error('Error fetching pipeline health', error)
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_HEALTH_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// Filter/Sort Actions
export const setFilters = (filters: any) => ({
  type: PIPELINE_ACTIONS.SET_FILTERS,
  payload: filters,
})

export const setSort = (sort: any) => ({
  type: PIPELINE_ACTIONS.SET_SORT,
  payload: sort,
})

export const clearFilters = () => ({
  type: PIPELINE_ACTIONS.CLEAR_FILTERS,
})

export const setPipelineEnvironment = (environment: 'staging' | 'prod') => {
  // Sync the env store immediately so any subsequent API calls use the right header
  setPipelineEnv(environment)
  return {
    type: PIPELINE_ACTIONS.SET_ENVIRONMENT,
    payload: environment,
  }
}

export const fetchEnvInfo = () => async (dispatch: any) => {
  try {
    const config = getPipelineAxiosConfig()
    const { data } = await axios.get(`${pipelineApiUrl}/env-info`, config)
    dispatch({ type: PIPELINE_ACTIONS.SET_ENV_INFO, payload: data })
    return data
  } catch {
    return null
  }
}

// UI Actions
export const setSelectedThread = (threadId: string | null) => ({
  type: PIPELINE_ACTIONS.SET_SELECTED_THREAD,
  payload: threadId,
})

export const setActiveTab = (tab: string) => ({
  type: PIPELINE_ACTIONS.SET_ACTIVE_TAB,
  payload: tab,
})

// HubSpot Actions
export const pushToHubSpot = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/hubspot/${accountId}/push`, {}, getPipelineAxiosConfig())

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error) {
      console.error('Error pushing to HubSpot', error)
      throw error
    }
  }
}

export const getHubSpotStatus = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/hubspot/${accountId}/status`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error getting HubSpot status', error)
      throw error
    }
  }
}

// Job Actions
export const triggerJob = (jobType: string, params: any = {}) => {
  return async () => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/jobs/${jobType}`, params, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error triggering job', error)
      throw error
    }
  }
}

/**
 * Request cancellation of a running pipeline job. Job will stop at the next phase boundary.
 */
export const cancelPipelineJob = (jobId: number) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/jobs/cancel/${jobId}`, {}, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error cancelling pipeline job', error)
      throw error
    }
  }
}

/**
 * Mark stale "running" jobs (e.g. older than 1 hour) as failed. Cleans up ghosts from restarts/hangs.
 */
export const markStalePipelineJobsAsFailed = (olderThanMinutes: number = 60) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/jobs/mark-stale-failed`,
        { olderThanMinutes },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error) {
      console.error('Error marking stale pipeline jobs as failed', error)
      throw error
    }
  }
}

/**
 * Fetch pipeline error/failure details
 */
export const fetchPipelineErrors = (limit: number = 20, jobName?: string) => {
  return async () => {
    try {
      const params = new URLSearchParams({ limit: String(limit) })
      if (jobName) params.append('job_name', jobName)
      const response = await axios.get(`${pipelineApiUrl}/health/errors?${params.toString()}`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error fetching pipeline errors', error)
      throw error
    }
  }
}

/**
 * Clean up old pipeline logs
 */
export const cleanupPipelineLogs = (days: number = 30) => {
  return async () => {
    try {
      const response = await axios.delete(
        `${pipelineApiUrl}/health/logs/cleanup?days=${days}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error) {
      console.error('Error cleaning up pipeline logs', error)
      throw error
    }
  }
}

export const generateAISummary = (accountId: string, scope: string = 'account') => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/summaries/generate/account/${accountId}`,
        { scope },
        getPipelineAxiosConfig()
      )

      // Check for graceful failure
      if (!response.data.success) {
        return response.data // Return error info, don't throw
      }

      // Dispatch the new summary to state if scope is 'account'
      if (scope === 'account') {
        dispatch({
          type: PIPELINE_ACTIONS.FETCH_SUMMARY_FULFILLED,
          payload: response.data.summary,
        })
      }

      return response.data
    } catch (error: any) {
      console.error('Error generating AI summary', error)
      // Return graceful error instead of throwing
      return {
        success: false,
        error: 'Request failed',
        message: error.message || 'Please try again later.',
      }
    }
  }
}

export const fetchScopedSummary = (accountId: string, scope: string) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/summaries/latest/scope/${accountId}/${scope}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error) {
      console.error('Error fetching scoped summary', error)
      return null
    }
  }
}

export const fetchSummaryHistory = (accountId: string, scope?: string) => {
  return async () => {
    try {
      const params = scope ? `?scope=${scope}` : ''
      const response = await axios.get(
        `${pipelineApiUrl}/summaries/history/account/${accountId}${params}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error) {
      console.error('Error fetching summary history', error)
      throw error
    }
  }
}

// Legacy - kept for backwards compatibility
export const triggerAISummary = (accountId: string) => {
  return generateAISummary(accountId)
}

/**
 * Generate ALL summaries for an account in a single AI call
 * Returns summaries for: overview, revenue, activity, customers, conversations
 */
export const generateAllSummaries = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/summaries/generate-all/${accountId}`,
        {},
        getPipelineAxiosConfig()
      )

      // Check for graceful failure
      if (!response.data.success) {
        return response.data // Return error info, don't throw
      }

      // Dispatch the account summary to state
      if (response.data.summaries?.account) {
        dispatch({
          type: PIPELINE_ACTIONS.FETCH_SUMMARY_FULFILLED,
          payload: response.data.summaries.account,
        })
      }

      return response.data
    } catch (error) {
      console.error('Error generating all summaries', error)
      throw error
    }
  }
}

/**
 * Fetch all summaries for an account (one per scope)
 */
export const fetchAllSummaries = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/summaries/all/${accountId}`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error fetching all summaries', error)
      throw error
    }
  }
}

// Matching Actions
export const fetchMatchCandidates = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/matching/${accountId}/candidates`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error fetching match candidates', error)
      throw error
    }
  }
}

export const autoMatchAccount = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/matching/${accountId}/auto`, {}, getPipelineAxiosConfig())

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error) {
      console.error('Error auto-matching account', error)
      throw error
    }
  }
}

export const applyMatch = (accountId: string, match: any, matchType: string = 'manual') => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/matching/${accountId}/apply`,
        { match, matchType },
        getPipelineAxiosConfig()
      )

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error) {
      console.error('Error applying match', error)
      throw error
    }
  }
}

export const removeMatch = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.delete(`${pipelineApiUrl}/matching/${accountId}`, getPipelineAxiosConfig())

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error) {
      console.error('Error removing match', error)
      throw error
    }
  }
}

export const autoMatchAllAccounts = () => {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/matching/auto-all`, {}, getPipelineAxiosConfig())

      // Refresh accounts list
      dispatch(fetchAccounts())

      return response.data
    } catch (error) {
      console.error('Error auto-matching all accounts', error)
      throw error
    }
  }
}

export const refreshPayHelmData = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/matching/${accountId}/refresh`, {}, getPipelineAxiosConfig())

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error) {
      console.error('Error refreshing PayHelm data', error)
      throw error
    }
  }
}

/**
 * Link an additional PayHelm account to a pipeline account.
 * Appends to payhelm_merchant_ids JSONB array.
 */
export const addPayhelmLink = (
  accountId: string,
  payload: { email: string; merchant_id?: string; channel?: string }
) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/add-payhelm-link`,
        payload,
        getPipelineAxiosConfig()
      )

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error) {
      console.error('Error adding PayHelm link', error)
      throw error
    }
  }
}

/**
 * Remove a linked PayHelm account from a pipeline account.
 * Removes the entry from payhelm_merchant_ids JSONB array.
 */
export const removePayhelmLink = (
  accountId: string,
  payload: { email: string; merchant_id?: string }
) => {
  return async (dispatch) => {
    try {
      const response = await axios.delete(
        `${pipelineApiUrl}/accounts/${accountId}/remove-payhelm-link`,
        {
          ...getPipelineAxiosConfig(),
          data: payload,
        }
      )

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error) {
      console.error('Error removing PayHelm link', error)
      throw error
    }
  }
}

export const fetchMatchingStats = () => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/matching/stats`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error fetching matching stats', error)
      throw error
    }
  }
}

// Account Merge & Dedup Actions
export const mergeAccounts = (sourceId: string, targetId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${sourceId}/merge-into/${targetId}`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error merging accounts', error)
      throw error
    }
  }
}

export const searchPipelineAccounts = (query: string) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/accounts?search=${encodeURIComponent(query)}&limit=10`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error searching pipeline accounts', error)
      throw error
    }
  }
}

export const detectDuplicates = (autoMerge: boolean = false) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/dedup`,
        { autoMerge },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error detecting duplicates', error)
      throw error
    }
  }
}

// Customer Profile Actions
export const fetchCustomerProfile = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/profile/${accountId}`, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      // 404 means not matched, which is expected for unmatched accounts
      if (error.response?.status === 404) {
        return null
      }
      console.error('Error fetching customer profile', error)
      throw error
    }
  }
}

// Revenue Actions
export const fetchRevenueOverview = (accountId: string, period: string = '30d') => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/revenue/${accountId}?period=${period}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching revenue overview', error)
      // Return error state instead of throwing
      return { success: false, error: 'Failed to load revenue data', data: null }
    }
  }
}

export const fetchTopProducts = (accountId: string, period: string = '30d', limit: number = 10) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/revenue/${accountId}/products?period=${period}&limit=${limit}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching top products', error)
      return { success: false, error: 'Failed to load product data', data: [] }
    }
  }
}

export const fetchRecentOrders = (accountId: string, limit: number = 20, offset: number = 0) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/revenue/${accountId}/orders?limit=${limit}&offset=${offset}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching recent orders', error)
      return { success: false, error: 'Failed to load order data', data: { orders: [], total: 0 } }
    }
  }
}

export const fetchRevenueBreakdowns = (accountId: string, period: string = '30d') => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/revenue/${accountId}/breakdowns?period=${period}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching revenue breakdowns', error)
      return { success: false, error: 'Failed to load breakdown data', data: null }
    }
  }
}

export const fetchDebugData = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/debug/${accountId}`, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      console.error('Error fetching debug data', error)
      return { error: 'Failed to load debug data. You may not have the required permission.' }
    }
  }
}

// Account Actions

export const fetchAvailableActions = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/actions/${accountId}/available`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error fetching available actions', error)
      return { actions: [] }
    }
  }
}

export const fetchStageHistory = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/stage-history/${accountId}`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error fetching stage history', error)
      return { history: [], total: 0 }
    }
  }
}

// Dashboard Actions
export const fetchDashboardData = () => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/dashboard`, getPipelineAxiosConfig())
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Error fetching dashboard data', error)
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to load dashboard data',
        status: error.response?.status,
      }
    }
  }
}

export const fetchTeamMembers = () => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/dashboard/team`, getPipelineAxiosConfig())
      return response.data
    } catch (error) {
      console.error('Error fetching team members', error)
      return { team_members: [] }
    }
  }
}

// Assignment Actions
export const updateAccountAssignment = (accountId: string, updates: { owner?: string; teamMembers?: string[] }) => {
  return async (dispatch) => {
    try {
      const response = await axios.patch(
        `${pipelineApiUrl}/accounts/${accountId}/assignment`,
        updates,
        getPipelineAxiosConfig()
      )

      // Refresh account data
      dispatch(fetchAccountDetail(accountId))

      return response.data
    } catch (error: any) {
      console.error('Error updating account assignment', error)
      throw error
    }
  }
}

// Update Account Fields
export const updateAccount = (
  accountId: string,
  updates: { name?: string; country?: string; utm_source?: string; [key: string]: any }
) => {
  return async (dispatch) => {
    try {
      const config = {
        ...getPipelineAxiosConfig(),
        headers: {
          ...getPipelineAxiosConfig().headers,
          'Content-Type': 'application/json',
        },
      }
      const response = await axios.patch(`${pipelineApiUrl}/accounts/${accountId}`, updates, config)

      // Refresh account data and merged people
      dispatch(fetchAccountDetail(accountId))
      dispatch(fetchPeople(accountId))

      return response.data
    } catch (error: any) {
      console.error('Error updating account', error)
      throw error
    }
  }
}

// Refresh people from Intercom for this account (fetch contacts for linked company and upsert)
export const refreshPeopleForAccount = (accountId: string) => {
  return async (dispatch) => {
    try {
      await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/refresh-people`,
        {},
        getPipelineAxiosConfig()
      )
      dispatch(fetchPeople(accountId))
      dispatch(fetchAccountDetail(accountId))
      return { success: true }
    } catch (error: any) {
      console.error('Error refreshing people', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to refresh people',
      }
    }
  }
}

// Sync activity data (conversations/threads) from Intercom for this account
export const syncActivityForAccount = (accountId: string) => {
  return async (dispatch) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/sync-activity`,
        {},
        getPipelineAxiosConfig()
      )
      // Refresh threads and events after sync
      dispatch(fetchThreads(accountId) as any)
      dispatch(fetchEvents(accountId) as any)
      dispatch(fetchAccountDetail(accountId))
      return { success: true, conversations: response.data?.conversations || 0 }
    } catch (error: any) {
      console.error('Error syncing activity', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to sync activity',
      }
    }
  }
}

export const fetchAccountAssignment = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/accounts/${accountId}/assignment`, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      console.error('Error fetching account assignment', error)
      return { owner: 'unassigned', team_members: [] }
    }
  }
}

// Opportunity Actions
export const fetchOpportunities = (filters: Record<string, any> = {}) => {
  return async (dispatch) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_OPPORTUNITIES_PENDING })

    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })

      const response = await axios.get(`${pipelineApiUrl}/opportunities?${params.toString()}`, getPipelineAxiosConfig())

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_OPPORTUNITIES_FULFILLED,
        payload: response.data,
      })

      return response.data
    } catch (error: any) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_OPPORTUNITIES_REJECTED,
        payload: error,
      })
      throw error
    }
  }
}

// ============================================
// PARTNERSHIP OPPORTUNITIES
// ============================================

export const fetchPartnerships = (filters: Record<string, any> = {}) => {
  return async (dispatch) => {
    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })

      const response = await axios.get(
        `${pipelineApiUrl}/opportunities/partnerships?${params.toString()}`,
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_OPPORTUNITIES_FULFILLED,
        payload: { ...response.data, type: 'partnership' },
      })

      return response.data
    } catch (error: any) {
      console.error('Error fetching partnerships', error)
      throw error
    }
  }
}

export const fetchPartnershipStats = () => {
  return async (dispatch) => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/opportunities/partnerships/stats`, getPipelineAxiosConfig())

      dispatch({
        type: PIPELINE_ACTIONS.FETCH_OPPORTUNITY_STATS_FULFILLED,
        payload: { ...response.data, type: 'partnership' },
      })

      return response.data
    } catch (error: any) {
      console.error('Error fetching partnership stats', error)
      throw error
    }
  }
}

export const updatePartnershipStatus = (
  id: number,
  status: string,
  updates: { owner?: string; next_action?: string; next_action_date?: string; notes?: string } = {}
) => {
  return async (dispatch) => {
    try {
      const response = await axios.patch(
        `${pipelineApiUrl}/opportunities/partnerships/${id}/status`,
        { status, ...updates },
        getPipelineAxiosConfig()
      )

      dispatch({
        type: PIPELINE_ACTIONS.UPDATE_OPPORTUNITY_STATUS_FULFILLED,
        payload: { ...response.data, type: 'partnership' },
      })

      return response.data
    } catch (error: any) {
      console.error('Error updating partnership status', error)
      throw error
    }
  }
}

// ============================================
// QUICK WIN OPPORTUNITIES
// ============================================

export const fetchQuickWins = (filters: Record<string, any> = {}) => {
  return async (dispatch) => {
    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })

      const response = await axios.get(
        `${pipelineApiUrl}/opportunities/quick-wins?${params.toString()}`,
        getPipelineAxiosConfig()
      )

      return response.data
    } catch (error: any) {
      console.error('Error fetching quick wins', error)
      throw error
    }
  }
}

export const fetchQuickWinStats = () => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/opportunities/quick-wins/stats`, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      console.error('Error fetching quick win stats', error)
      throw error
    }
  }
}

export const updateQuickWinStatus = (id: number, status: string) => {
  return async () => {
    try {
      const response = await axios.patch(
        `${pipelineApiUrl}/opportunities/quick-wins/${id}/status`,
        { status },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error updating quick win status', error)
      throw error
    }
  }
}

// ============================================
// ACCOUNT-LEVEL OPPORTUNITIES
// ============================================

export const fetchAccountOpportunities = (accountId: string, includeHistory = false) => {
  return async () => {
    try {
      const params = includeHistory ? '?include_history=true' : ''
      const response = await axios.get(
        `${pipelineApiUrl}/opportunities/${accountId}${params}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching account opportunities', error)
      return { success: false, data: { partnership: null, quick_wins: [] } }
    }
  }
}

export const analyzeAccountOpportunities = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities/${accountId}/refresh`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error analyzing account opportunities', error)
      throw error
    }
  }
}

export const analyzeAllOpportunities = (limit = 100, offset = 0) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities/analyze-all`,
        { limit, offset },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error analyzing all opportunities', error)
      throw error
    }
  }
}

export const generatePartnershipOpportunity = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities/${accountId}/generate-partnership`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error generating partnership opportunity', error)
      throw error
    }
  }
}

// Legacy - kept for backwards compatibility
export const fetchOpportunityStats = (filters: Record<string, any> = {}) => fetchPartnershipStats()
export const updateOpportunityStatus = (opportunityId: number, status: string) =>
  updatePartnershipStatus(opportunityId, status)

// ============================================
// OPPORTUNITIES V2 - Unified Model
// ============================================

export const fetchOpportunitiesV2 = (
  accountId: string,
  options?: { status?: string[]; type?: string[]; includeArchived?: boolean }
) => {
  return async () => {
    try {
      const params = new URLSearchParams()
      if (options?.status?.length) params.append('status', options.status.join(','))
      if (options?.type?.length) params.append('type', options.type.join(','))
      if (options?.includeArchived) params.append('includeArchived', 'true')

      const response = await axios.get(
        `${pipelineApiUrl}/opportunities-v2/${accountId}${params.toString() ? '?' + params.toString() : ''}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching opportunities v2', error)
      return { opportunities: [], total: 0 }
    }
  }
}

export const fetchOpportunityDetailV2 = (opportunityId: string) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/opportunities-v2/detail/${opportunityId}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching opportunity detail', error)
      throw error
    }
  }
}

export const createOpportunityV2 = (data: {
  account_id: string
  opportunity_type: string
  opportunity_subtype?: string
  title: string
  description?: string
  estimated_value?: number
  confidence?: string
  target_date?: string
  next_action?: string
  notes?: string
  tasks?: Array<{ title: string; description?: string; phase?: number }>
}) => {
  return async () => {
    try {
      const response = await axios.post(`${pipelineApiUrl}/opportunities-v2`, data, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      console.error('Error creating opportunity', error)
      throw error
    }
  }
}

export const updateOpportunityV2 = (opportunityId: string, updates: Record<string, any>) => {
  return async () => {
    try {
      const response = await axios.put(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}`,
        updates,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error updating opportunity', error)
      throw error
    }
  }
}

export const activateOpportunityV2 = (opportunityId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/activate`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error activating opportunity', error)
      throw error
    }
  }
}

export const researchOpportunity = (opportunityId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/research`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error researching opportunity', error)
      throw error
    }
  }
}

export const archiveOpportunityV2 = (opportunityId: string, reason: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/archive`,
        { reason },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error archiving opportunity', error)
      throw error
    }
  }
}

export const closeOpportunityV2 = (opportunityId: string, status: 'won' | 'lost', reason?: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/close`,
        { status, reason },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error closing opportunity', error)
      throw error
    }
  }
}

export const detectOpportunitiesV2 = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/detect/${accountId}`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error detecting opportunities', error)
      throw error
    }
  }
}

// Refresh opportunities - detects AND saves suggestions to database
export const refreshOpportunitiesV2 = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/refresh/${accountId}`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error refreshing opportunities', error)
      throw error
    }
  }
}

// Task actions
export const fetchOpportunityTasksV2 = (opportunityId: string) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/tasks`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching tasks', error)
      return { tasks: [], total: 0 }
    }
  }
}

export const createOpportunityTaskV2 = (
  opportunityId: string,
  data: { title: string; description?: string; phase?: number }
) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/tasks`,
        data,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error creating task', error)
      throw error
    }
  }
}

export const updateOpportunityTaskV2 = (taskId: string, updates: { title?: string; status?: string }) => {
  return async () => {
    try {
      const response = await axios.put(
        `${pipelineApiUrl}/opportunities-v2/tasks/${taskId}`,
        updates,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error updating task', error)
      throw error
    }
  }
}

export const deleteOpportunityTaskV2 = (taskId: string) => {
  return async () => {
    try {
      const response = await axios.delete(
        `${pipelineApiUrl}/opportunities-v2/tasks/${taskId}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error deleting task', error)
      throw error
    }
  }
}

// Note actions
export const fetchOpportunityNotesV2 = (opportunityId: string) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/notes`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching notes', error)
      return { notes: [], total: 0 }
    }
  }
}

export const createOpportunityNoteV2 = (opportunityId: string, content: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/${opportunityId}/notes`,
        { content },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error creating note', error)
      throw error
    }
  }
}

// ==========================================
// Dashboard V2 Actions
// ==========================================

export const fetchDashboardOpportunities = (options?: {
  status?: string
  type?: string
  limit?: number
  offset?: number
}) => {
  return async () => {
    try {
      const params = new URLSearchParams()
      if (options?.status) params.append('status', options.status)
      if (options?.type) params.append('type', options.type)
      if (options?.limit) params.append('limit', String(options.limit))
      if (options?.offset) params.append('offset', String(options.offset))

      const response = await axios.get(
        `${pipelineApiUrl}/opportunities-v2/dashboard/list${params.toString() ? '?' + params.toString() : ''}`,
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error fetching dashboard opportunities', error)
      return { opportunities: [], total: 0 }
    }
  }
}

export const fetchDashboardStats = () => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/opportunities-v2/dashboard/stats`, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      console.error('Error fetching dashboard stats', error)
      return { total: 0, by_status: {}, by_type: {}, total_value: 0, active_count: 0, suggested_count: 0 }
    }
  }
}

export const refreshAllOpportunities = (limit: number = 50) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/opportunities-v2/dashboard/refresh-all`,
        { limit },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error refreshing all opportunities', error)
      throw error
    }
  }
}

// =============================================
// Pipeline Data Management Actions
// =============================================

/**
 * Preview or execute cleanup of unlinked (Intercom-only) accounts
 */
export const previewUnlinkedAccounts = () => {
  return async () => {
    const response = await axios.post(
      `${pipelineApiUrl}/jobs/cleanup/unlinked-accounts?preview=true`,
      {},
      getPipelineAxiosConfig()
    )
    return response.data
  }
}

export const cleanupUnlinkedAccounts = () => {
  return async () => {
    const response = await axios.post(
      `${pipelineApiUrl}/jobs/cleanup/unlinked-accounts`,
      {},
      getPipelineAxiosConfig()
    )
    return response.data
  }
}

/**
 * Bulk add PayHelm users to the pipeline
 */
export const bulkAddPayHelmUsers = (users: Array<{ unique_user_id: string; email: string }>) => {
  return async () => {
    const response = await axios.post(
      `${pipelineApiUrl}/accounts/bulk-add-payhelm-users`,
      { users },
      getPipelineAxiosConfig()
    )
    return response.data
  }
}

/**
 * Debug matching for an account - shows why matches aren't being found
 */
export const debugAccountMatching = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/matching/${accountId}/debug`, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      console.error('Error debugging account matching', error)
      throw error
    }
  }
}

// =============================================
// Public Company Lookup Actions
// =============================================

/**
 * Check all accounts for publicly traded company status
 * Uses SEC EDGAR data to identify US public companies
 */
export const checkPublicCompanies = (options?: { onlyUnchecked?: boolean; limit?: number }) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/jobs/check-public-companies`,
        options || {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error checking public companies', error)
      throw error
    }
  }
}

/**
 * Check if a specific account is a publicly traded company
 */
export const checkAccountPublicStatus = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/jobs/check-public-company/${accountId}`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error checking account public status', error)
      throw error
    }
  }
}

/**
 * Get summary of publicly traded accounts
 */
export const getPublicCompaniesSummary = () => {
  return async () => {
    try {
      const response = await axios.get(`${pipelineApiUrl}/jobs/public-companies`, getPipelineAxiosConfig())
      return response.data
    } catch (error: any) {
      console.error('Error getting public companies summary', error)
      throw error
    }
  }
}

/**
 * Fetch existing company profile (AI-generated enrichment) for an account
 */
export const fetchCompanyProfile = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/accounts/${accountId}/company-profile`,
        getPipelineAxiosConfig()
      )
      return response.data // { profile, generated_at } or null
    } catch (error: any) {
      console.error('Error fetching company profile', error)
      return null
    }
  }
}

/**
 * Enrich company profile using AI (generates description, industry, social links, etc.)
 */
export const enrichCompanyProfile = (accountId: string, force?: boolean) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/enrich-company`,
        { force: force || false },
        { ...getPipelineAxiosConfig(), timeout: 60000 }
      )
      return response.data // { success, profile, generated_at, cached }
    } catch (error: any) {
      console.error('Error enriching company profile', error)
      const msg = error.code === 'ECONNABORTED'
        ? 'Request timed out - AI may be slow, try again'
        : error.response?.data?.error || 'Failed to enrich company profile'
      return { success: false, error: msg }
    }
  }
}

// Intelligence Enrichment Actions
export const enrichMarketplace = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/enrich-marketplace`,
        {},
        { ...getPipelineAxiosConfig(), timeout: 60000 }
      )
      return response.data
    } catch (error: any) {
      console.error('Error enriching marketplace presence', error)
      const msg = error.code === 'ECONNABORTED'
        ? 'Request timed out - AI may be slow, try again'
        : error.response?.data?.error || 'Failed to research marketplace presence'
      return { success: false, error: msg }
    }
  }
}

export const overrideMarketplaceStatus = (accountId: string, marketplace: string, status: string) => {
  return async () => {
    try {
      const response = await axios.patch(
        `${pipelineApiUrl}/accounts/${accountId}/marketplace-override`,
        { marketplace, status },
        { ...getPipelineAxiosConfig(), timeout: 10000 }
      )
      return response.data
    } catch (error: any) {
      console.error('Error overriding marketplace status', error)
      return { success: false, error: error.response?.data?.error || 'Failed to update marketplace status' }
    }
  }
}

export const enrichStripe = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/enrich-stripe`,
        {},
        { ...getPipelineAxiosConfig(), timeout: 30000 }
      )
      return response.data
    } catch (error: any) {
      console.error('Error enriching Stripe data', error)
      const msg = error.code === 'ECONNABORTED'
        ? 'Request timed out, try again'
        : error.response?.data?.error || 'Failed to enrich Stripe data'
      return { success: false, error: msg }
    }
  }
}

export const enrichConnectionStatus = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/enrich-status`,
        {},
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error checking connection status', error)
      return { success: false, error: error.response?.data?.error || 'Failed to check connection status' }
    }
  }
}

export const detectAccountAgencies = (accountId: string) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/detect-agencies`,
        {},
        { ...getPipelineAxiosConfig(), timeout: 60000 }
      )
      return response.data
    } catch (error: any) {
      console.error('Error detecting agencies', error)
      const msg = error.code === 'ECONNABORTED'
        ? 'Request timed out - AI may be slow, try again'
        : error.response?.data?.error || 'Failed to detect agencies'
      return { success: false, error: msg }
    }
  }
}

// ─── Advisor (Sales Co-Pilot) Actions ────────────────────────

export const fetchAdvisorLatest = (accountId: string) => {
  return async (dispatch: any) => {
    dispatch({ type: PIPELINE_ACTIONS.FETCH_ADVISOR_LATEST_PENDING })
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/latest`,
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ADVISOR_LATEST_FULFILLED,
        payload: response.data.insight,
      })
      return response.data
    } catch (error: any) {
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ADVISOR_LATEST_REJECTED,
        payload: error.response?.data?.error || 'Failed to fetch advisor insight',
      })
      return null
    }
  }
}

export const generateAdvisorInsights = (accountId: string, force = false) => {
  return async (dispatch: any) => {
    dispatch({ type: PIPELINE_ACTIONS.GENERATE_ADVISOR_PENDING })
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/generate`,
        { force },
        { ...getPipelineAxiosConfig(), timeout: 60000 }
      )
      dispatch({
        type: PIPELINE_ACTIONS.GENERATE_ADVISOR_FULFILLED,
        payload: response.data,
      })
      return response.data
    } catch (error: any) {
      const msg = error.code === 'ECONNABORTED'
        ? 'Request timed out — AI may be slow, try again'
        : error.response?.data?.error || 'Failed to generate insights'
      dispatch({
        type: PIPELINE_ACTIONS.GENERATE_ADVISOR_REJECTED,
        payload: msg,
      })
      return { success: false, error: msg }
    }
  }
}

export const fetchAdvisorHistory = (accountId: string, limit = 10) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/history?limit=${limit}`,
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ADVISOR_HISTORY_FULFILLED,
        payload: response.data.history,
      })
      return response.data
    } catch (error: any) {
      console.error('Error fetching advisor history', error)
      return { success: false, error: error.response?.data?.error || 'Failed to fetch history' }
    }
  }
}

export const submitAdvisorFeedback = (accountId: string, rating: number, notes?: string, tags?: string[]) => {
  return async () => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/feedback`,
        { rating, notes, tags },
        getPipelineAxiosConfig()
      )
      return response.data
    } catch (error: any) {
      console.error('Error submitting advisor feedback', error)
      return { success: false }
    }
  }
}

export const logAdvisorEvent = (accountId: string, eventType: string, payload?: any) => {
  return async () => {
    try {
      await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/event`,
        { event_type: eventType, payload },
        getPipelineAxiosConfig()
      )
    } catch {
      // fire-and-forget
    }
  }
}

// ============================================
// ADVISOR CHAT CONVERSATIONS
// ============================================

export const fetchChatConversations = (accountId: string) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/chat/conversations`,
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_CONVERSATIONS_FULFILLED,
        payload: response.data.conversations,
      })
      return response.data
    } catch (error: any) {
      console.error('Error fetching chat conversations', error)
      return { success: false }
    }
  }
}

export const createChatConversation = (accountId: string, title?: string) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/chat/conversations`,
        { title },
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.CREATE_CONVERSATION_FULFILLED,
        payload: response.data.conversation,
      })
      dispatch({
        type: PIPELINE_ACTIONS.SET_ACTIVE_CONVERSATION,
        payload: response.data.conversation.id,
      })
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ADVISOR_CHAT_HISTORY_FULFILLED,
        payload: [],
      })
      return response.data.conversation
    } catch (error: any) {
      console.error('Error creating chat conversation', error)
      return null
    }
  }
}

export const deleteChatConversation = (accountId: string, conversationId: string) => {
  return async (dispatch: any) => {
    try {
      await axios.delete(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/chat/conversations/${conversationId}`,
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.DELETE_CONVERSATION_FULFILLED,
        payload: conversationId,
      })
      return { success: true }
    } catch (error: any) {
      console.error('Error deleting conversation', error)
      return { success: false }
    }
  }
}

export const renameChatConversation = (accountId: string, conversationId: string, title: string) => {
  return async (dispatch: any) => {
    try {
      await axios.patch(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/chat/conversations/${conversationId}`,
        { title },
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.RENAME_CONVERSATION_FULFILLED,
        payload: { conversationId, title },
      })
      return { success: true }
    } catch (error: any) {
      console.error('Error renaming conversation', error)
      return { success: false }
    }
  }
}

export const setActiveConversation = (conversationId: string | null) => {
  return (dispatch: any) => {
    dispatch({
      type: PIPELINE_ACTIONS.SET_ACTIVE_CONVERSATION,
      payload: conversationId,
    })
  }
}

// ============================================
// ADVISOR CHAT MESSAGES
// ============================================

export const sendAdvisorChat = (accountId: string, message: string, conversationId: string) => {
  return async (dispatch: any) => {
    dispatch({
      type: PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_PENDING,
      payload: { message },
    })
    try {
      const response = await axios.post(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/chat`,
        { message, conversation_id: conversationId },
        { ...getPipelineAxiosConfig(), timeout: 60000 }
      )
      if (response.data.success === false) {
        dispatch({
          type: PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_REJECTED,
          payload: response.data.error || 'Chat failed',
        })
        return response.data
      }
      dispatch({
        type: PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_FULFILLED,
        payload: {
          id: response.data.message_id,
          role: 'assistant',
          content: response.data.reply,
          created_at: response.data.created_at,
        },
      })
      return response.data
    } catch (error: any) {
      const msg = error.code === 'ECONNABORTED'
        ? 'Request timed out — AI may be slow, try again'
        : error.response?.data?.error || 'Chat failed'
      dispatch({
        type: PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_REJECTED,
        payload: msg,
      })
      return { success: false, error: msg }
    }
  }
}

export const sendAdvisorChatStream = (
  accountId: string,
  message: string,
  conversationId: string,
  onChunk: (fullContent: string) => void,
  onStatus: (msg: string) => void,
) => {
  return async (dispatch: any) => {
    dispatch({
      type: PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_PENDING,
      payload: { message },
    })
    try {
      const config = getPipelineAxiosConfig()
      const response = await fetch(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/chat/stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...config.headers,
          },
          body: JSON.stringify({ message, conversation_id: conversationId }),
          credentials: 'include',
        }
      )

      if (!response.ok) throw new Error('Stream request failed')
      if (!response.body) throw new Error('No response body')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let streamContent = ''
      let messageId = ''
      let createdAt = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const payload = JSON.parse(line.slice(6))
            if (payload.type === 'chunk') {
              streamContent += payload.content
              onChunk(streamContent)
            } else if (payload.type === 'status') {
              onStatus(payload.message)
            } else if (payload.type === 'done') {
              messageId = payload.message_id
              createdAt = payload.created_at
            } else if (payload.type === 'error') {
              throw new Error(payload.message)
            }
          } catch (parseErr: any) {
            if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr
          }
        }
      }

      dispatch({
        type: PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_FULFILLED,
        payload: {
          id: messageId,
          role: 'assistant',
          content: streamContent,
          created_at: createdAt,
        },
      })
      return { success: true }
    } catch (error: any) {
      const msg = error.message || 'Chat failed'
      dispatch({
        type: PIPELINE_ACTIONS.SEND_ADVISOR_CHAT_REJECTED,
        payload: msg,
      })
      return { success: false, error: msg }
    }
  }
}

export const fetchAdvisorChatHistory = (accountId: string, conversationId: string, limit = 50) => {
  return async (dispatch: any) => {
    try {
      const response = await axios.get(
        `${pipelineApiUrl}/accounts/${accountId}/advisor/chat/history?conversation_id=${conversationId}&limit=${limit}`,
        getPipelineAxiosConfig()
      )
      dispatch({
        type: PIPELINE_ACTIONS.FETCH_ADVISOR_CHAT_HISTORY_FULFILLED,
        payload: response.data.messages,
      })
      return response.data
    } catch (error: any) {
      console.error('Error fetching advisor chat history', error)
      return { success: false }
    }
  }
}

export const clearAdvisorChatHistory = () => {
  return (dispatch: any) => {
    dispatch({ type: PIPELINE_ACTIONS.CLEAR_ADVISOR_CHAT_HISTORY })
  }
}
